import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  CircularProgress,
  Divider,
  Autocomplete,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import { format, addDays, subDays, startOfDay, isToday } from 'date-fns';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import { useAuthStore } from '@/stores/authStore';
import { useShiftSchedule, type ComputedShift } from '@/hooks/useShifts';
import { useCollectionSync } from '@/hooks/useEntitySync';
import { useNotify } from '@/hooks/useNotify';
import {
  useTasksList,
  useStartTask,
  usePauseTask,
  useResumeTask,
  useCompleteTask,
  useClaimTask,
  type TaskListItem,
} from '@/hooks/useTasks';
import {
  useMyServiceTasks,
  type MyServiceTask,
} from '@/hooks/useWorkOrders';
import { useStaffUsers } from '@/hooks/useStaffUsers';
import { formatDuration } from '@/utils/duration';
import { orderStaffForPicker } from '@/utils/staffNames';
import { CreateTaskDialog } from './CreateTaskDialog';
import { EditTaskDialog } from './EditTaskDialog';
import { PriorityChip } from '@/components/tasks/PriorityChip';
import { getTaskCardStyles } from './taskCardStyles';
import { WorkOrderGroupCard } from './WorkOrderGroupCard';
import { WorkOrderNotesButton } from '@/components/workorders/WorkOrderNotesButton';
import { mergeAssignedGeneralTasks } from './buildDayTaskSections';
import { buildServiceSections } from './buildServiceSections';
import { queryKeys } from '@/hooks/queryKeys';
import {
  audienceLabelKey,
  defaultAudienceListFilter,
  TASK_AUDIENCE_FILTER_OPTIONS,
  type TaskAudienceFilter,
} from './taskAudience';
import { dateFnsLocale, intlLocale } from '@/utils/locale';

type UnifiedItem =
  | { type: 'task'; data: TaskListItem }
  | { type: 'woGroup'; workOrderId: string; services: MyServiceTask[] };

type TaskListContext = 'myDay' | 'openGeneral';

export function getTaskStatusActionVisibility(
  task: Pick<TaskListItem, 'assignedTo' | 'status'>,
  context: TaskListContext,
) {
  const statusActionsAllowed = context === 'myDay';

  return {
    showStart: statusActionsAllowed && task.status === 'pending' && Boolean(task.assignedTo),
    showPauseComplete: statusActionsAllowed && task.status === 'in_progress',
    showResumeComplete: statusActionsAllowed && task.status === 'paused',
  };
}

const taskStatusChipColors: Record<string, 'default' | 'primary' | 'warning' | 'success' | 'error'> = {
  pending: 'default',
  confirmed: 'primary',
  in_progress: 'primary',
  paused: 'warning',
  completed: 'success',
  cancelled: 'error',
};

export default function MyTasksPage() {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const notify = useNotify();
  const userId = useAuthStore((s) => s.user?.id);
  const userRole = useAuthStore((s) => s.user?.role);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canViewOthers = hasPermission('hr.view_team');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editTask, setEditTask] = useState<TaskListItem | null>(null);
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [selectedStaffUserId, setSelectedStaffUserId] = useState<string | null>(null);
  const [expandedWorkOrders, setExpandedWorkOrders] = useState<Set<string>>(() => new Set());
  const [audienceFilter, setAudienceFilter] = useState<TaskAudienceFilter>(
    () => defaultAudienceListFilter(userRole) ?? 'all',
  );

  const toggleWorkOrderExpanded = useCallback((workOrderId: string) => {
    setExpandedWorkOrders((prev) => {
      const next = new Set(prev);
      if (next.has(workOrderId)) next.delete(workOrderId);
      else next.add(workOrderId);
      return next;
    });
  }, []);

  // Effective user whose day is shown: selector for admins, otherwise self
  const viewUserId = canViewOthers && selectedStaffUserId ? selectedStaffUserId : userId;

  const staffUsersQuery = useStaffUsers({ isActive: true });
  // Mechanics first, alphabetical within each group (#3408).
  const staffUsers = useMemo(
    () => orderStaffForPicker(staffUsersQuery.data?.users ?? []),
    [staffUsersQuery.data?.users],
  );
  const selectedStaffUser = useMemo(
    () => staffUsers.find((u) => u.id === viewUserId) ?? null,
    [staffUsers, viewUserId],
  );
  // Acting from someone else's day starts, pauses and resumes that person's
  // timer, not the viewer's (#3281). The own day sends nothing.
  const timerOwner = useMemo(
    () =>
      selectedStaffUser && viewUserId !== userId
        ? {
            id: selectedStaffUser.id,
            name: `${selectedStaffUser.firstName} ${selectedStaffUser.lastName}`.trim(),
          }
        : null,
    [selectedStaffUser, viewUserId, userId],
  );

  const isSelectedToday = isToday(selectedDate);

  const handlePrevDay = useCallback(() => setSelectedDate((d) => subDays(d, 1)), []);
  const handleNextDay = useCallback(() => setSelectedDate((d) => addDays(d, 1)), []);
  const handleGoToday = useCallback(() => setSelectedDate(startOfDay(new Date())), []);

  const dateLabel = useMemo(() => {
    if (isSelectedToday) return t('myTasks.today');
    return format(selectedDate, 'EEEE d.M.yyyy', { locale: dateFnsLocale() });
  }, [selectedDate, isSelectedToday, t]);

  // Date range for selected date
  const dayStart = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, [selectedDate]);

  const dayEnd = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  }, [selectedDate]);

  // Shift schedule for selected date
  const shiftDateStr = useMemo(() => format(selectedDate, 'yyyy-MM-dd'), [selectedDate]);
  const shiftQuery = useShiftSchedule({
    startDate: shiftDateStr,
    endDate: shiftDateStr,
    userId: viewUserId,
  });
  const myShift = useMemo<ComputedShift | null>(() => {
    const shifts = shiftQuery.data ?? [];
    return shifts.find((s) => s.userId === viewUserId) ?? null;
  }, [shiftQuery.data, viewUserId]);

  // WebSocket: real-time updates for tasks and work orders. Without
  // invalidationKeys these subscriptions were no-ops, so the list only
  // refreshed on a manual reload (issue #427). Wire them to refetch the
  // task list and my-services list on the relevant lifecycle events.
  useCollectionSync('task', {
    invalidationKeys: [{ queryKey: queryKeys.tasks.lists() }],
  });
  useCollectionSync('workorder', {
    relevantScopes: ['services', 'status'],
    invalidationKeys: [{ queryKey: queryKeys.workOrders.myServices() }],
  });

  // Fetch tasks assigned to viewed user for selected day
  const myTasksQuery = useTasksList(
    { assignedToId: viewUserId, dateFrom: dayStart, dateTo: dayEnd, limit: 50 },
    { enabled: !!viewUserId },
  );

  // Fetch ongoing/paused tasks for viewed user — only when viewing today
  const ongoingTasksQuery = useTasksList(
    { assignedToId: viewUserId, status: 'in_progress', limit: 50 },
    { enabled: !!viewUserId && isSelectedToday },
  );

  const pausedTasksQuery = useTasksList(
    { assignedToId: viewUserId, status: 'paused', limit: 50 },
    { enabled: !!viewUserId && isSelectedToday },
  );

  // Fetch all open general tasks (team-wide visibility, any assignee, any date)
  const openGeneralTasksQuery = useTasksList({
    type: 'general',
    openOnly: true,
    limit: 50,
    ...(audienceFilter === 'all' ? {} : { audience: audienceFilter }),
  });

  // Surface assigned general tasks on today's left list so unscheduled ones aren't missed.
  const assignedGeneralTasksQuery = useTasksList(
    { assignedToId: viewUserId, type: 'general', openOnly: true, limit: 50 },
    { enabled: !!viewUserId && isSelectedToday },
  );

  // Fetch service tasks for viewed user
  const myServicesQuery = useMyServiceTasks(
    { dateFrom: dayStart, dateTo: dayEnd, userId: viewUserId },
    { enabled: !!viewUserId },
  );

  const ongoingServicesQuery = useMyServiceTasks(
    { status: 'in_progress', userId: viewUserId },
    { enabled: !!viewUserId && isSelectedToday },
  );

  const pausedServicesQuery = useMyServiceTasks(
    { status: 'paused', userId: viewUserId },
    { enabled: !!viewUserId && isSelectedToday },
  );

  // Task mutations
  const startTask = useStartTask();
  const pauseTask = usePauseTask();
  const resumeTask = useResumeTask();
  const completeTask = useCompleteTask();
  const claimTask = useClaimTask();

  // Build unified lists — group services by work order
  const ongoingTaskPages = ongoingTasksQuery.data?.pages;
  const pausedTaskPages = pausedTasksQuery.data?.pages;
  const myTaskPages = myTasksQuery.data?.pages;
  const assignedGeneralTaskPages = assignedGeneralTasksQuery.data?.pages;

  const myServices = myServicesQuery.data?.data;
  const ongoingServices = ongoingServicesQuery.data?.data;
  const pausedServices = pausedServicesQuery.data?.data;

  // Group every work order's services exactly once so starting one service
  // never splits its siblings into a different card (issue #428).
  const serviceSections = useMemo(
    () =>
      buildServiceSections(
        myServices ?? [],
        ongoingServices ?? [],
        pausedServices ?? [],
        isSelectedToday,
      ),
    [myServices, ongoingServices, pausedServices, isSelectedToday],
  );

  const ongoingItems = useMemo<UnifiedItem[]>(() => {
    if (!isSelectedToday) return [];

    const ongoingTasks = ongoingTaskPages?.flatMap((p) => p.data) ?? [];
    const pausedTasks = pausedTaskPages?.flatMap((p) => p.data) ?? [];

    const seen = new Set<string>();
    const items: UnifiedItem[] = [];

    for (const task of [...ongoingTasks, ...pausedTasks]) {
      if (!seen.has(task.id)) {
        seen.add(task.id);
        items.push({ type: 'task', data: task });
      }
    }

    items.push(...serviceSections.ongoing);

    return items;
  }, [isSelectedToday, ongoingTaskPages, pausedTaskPages, serviceSections]);

  const { scheduledItems, completedItems } = useMemo(() => {
    const myTasks = myTaskPages?.flatMap((p) => p.data) ?? [];
    const assignedGeneralTasks = isSelectedToday
      ? assignedGeneralTaskPages?.flatMap((p) => p.data) ?? []
      : [];
    const combinedTasks = mergeAssignedGeneralTasks(
      myTasks,
      assignedGeneralTasks,
      viewUserId,
    );
    const ongoingTaskIds = new Set(
      ongoingItems
        .filter((i): i is Extract<UnifiedItem, { type: 'task' }> => i.type === 'task')
        .map((i) => i.data.id),
    );

    const scheduled: UnifiedItem[] = [];
    const completed: UnifiedItem[] = [];

    for (const task of combinedTasks) {
      if (ongoingTaskIds.has(task.id) || task.status === 'cancelled') continue;
      if (task.status === 'in_progress' || task.status === 'paused') continue;
      if (task.status === 'completed') {
        completed.push({ type: 'task', data: task });
      } else {
        scheduled.push({ type: 'task', data: task });
      }
    }

    scheduled.push(...serviceSections.scheduled);
    completed.push(...serviceSections.completed);

    return { scheduledItems: scheduled, completedItems: completed };
  }, [
    myTaskPages,
    assignedGeneralTaskPages,
    isSelectedToday,
    viewUserId,
    ongoingItems,
    serviceSections,
  ]);

  const openGeneralPages = openGeneralTasksQuery.data?.pages;

  const openGeneralItems = useMemo<UnifiedItem[]>(() => {
    const tasks = openGeneralPages?.flatMap((p) => p.data) ?? [];
    return tasks.map((task) => ({ type: 'task' as const, data: task }));
  }, [openGeneralPages]);

  const isMyDayLoading =
    myTasksQuery.isLoading ||
    ongoingTasksQuery.isLoading ||
    assignedGeneralTasksQuery.isLoading ||
    myServicesQuery.isLoading;
  const isOpenGeneralLoading = openGeneralTasksQuery.isLoading;

  // Recreated each render on purpose: wrapping this in useCallback with the
  // mutation objects (what exhaustive-deps wants) would still churn every
  // render, and a partial dep list is how a click would act on a stale id.
  const handleTaskAction = async (action: string, taskId: string) => {
    try {
      switch (action) {
        case 'start':
          await startTask.mutateAsync(taskId);
          notify.success(t('myTasks.started'));
          break;
        case 'pause':
          await pauseTask.mutateAsync(taskId);
          notify.success(t('myTasks.paused'));
          break;
        case 'resume':
          await resumeTask.mutateAsync(taskId);
          notify.success(t('myTasks.resumed'));
          break;
        case 'complete':
          await completeTask.mutateAsync(taskId);
          notify.success(t('myTasks.completed'));
          break;
        case 'claim':
          await claimTask.mutateAsync(taskId);
          notify.success(t('myTasks.claimed'));
          break;
      }
    } catch (error) {
      notify.error(describeApiError(error, tc, { fallback: t('myTasks.actionFailed') }));
    }
  };

  const renderTaskCard = (task: TaskListItem, context: TaskListContext) => {
    const isDeadlinePassed = task.deadline && new Date(task.deadline) < new Date();
    const statusActionVisibility = getTaskStatusActionVisibility(task, context);

    const handleCardClick = () => {
      navigate(`/tasks/${task.id}`);
    };

    return (
      <Card
        key={task.id}
        sx={getTaskCardStyles(task.priority)}
        onClick={handleCardClick}
      >
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <TaskAltIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {task.title}
              </Typography>
              {task.description && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {task.description}
                </Typography>
              )}
              {task.note && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}
                >
                  {t('myTasks.notePrefix')}: {task.note}
                </Typography>
              )}
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                <PriorityChip priority={task.priority} />
                <Chip
                  size="small"
                  label={t(`status.${task.status}`, task.status)}
                  color={taskStatusChipColors[task.status] ?? 'default'}
                />
                {task.audience && task.audience !== 'anyone' && (
                  <Chip
                    size="small"
                    label={t(audienceLabelKey(task.audience))}
                    variant="outlined"
                  />
                )}
                {task.assignedTo ? (
                  <Chip
                    size="small"
                    label={`${task.assignedTo.firstName} ${task.assignedTo.lastName}`}
                    variant="outlined"
                  />
                ) : (
                  <Chip size="small" label={t('myTasks.unassigned')} variant="outlined" color="warning" />
                )}
                {task.scheduledAt && (
                  <Chip
                    size="small"
                    icon={<AccessTimeIcon />}
                    label={new Date(task.scheduledAt).toLocaleTimeString(intlLocale(), { hour: '2-digit', minute: '2-digit' })}
                    variant="outlined"
                  />
                )}
                {task.estimatedDurationHours != null && (
                  <Chip
                    size="small"
                    label={formatDuration(task.estimatedDurationHours)}
                    variant="outlined"
                  />
                )}
                {task.deadline && (
                  <Chip
                    size="small"
                    label={isDeadlinePassed ? t('myTasks.deadlinePassed') : t('myTasks.deadline')}
                    color={isDeadlinePassed ? 'error' : 'default'}
                    variant="outlined"
                  />
                )}
              </Stack>
              {task.booking && (
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ cursor: 'pointer', mt: 0.5, display: 'block' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/workorders/${task.booking!.id}`);
                  }}
                >
                  {task.booking.vehicleRegNumber} — {task.booking.serviceType}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {t('myTasks.createdBy')}: {task.createdBy.firstName} {task.createdBy.lastName}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
              <Tooltip title={t('myTasks.editTask')}>
                <IconButton
                  size="small"
                  aria-label={t('myTasks.editTask')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditTask(task);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {task.booking && (
                <WorkOrderNotesButton bookingId={task.booking.id} />
              )}
              {!task.assignedTo && (
                <Tooltip title={t('myTasks.claim')}>
                  <IconButton
                    size="small"
                    aria-label={t('myTasks.claim')}
                    onClick={() => {
                      void handleTaskAction('claim', task.id);
                    }}
                  >
                    <PersonAddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {statusActionVisibility.showStart && (
                task.booking?.status && ['quote', 'pending', 'confirmed', 'quote_rejected'].includes(task.booking.status) ? (
                  <Tooltip title={t('myTasks.vehicleNotArrived')}>
                    <span>
                      <IconButton
                        disabled
                        size="small"
                        color="primary"
                        aria-label={t('myTasks.vehicleNotArrived')}
                      >
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title={t('myTasks.start')}>
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label={t('myTasks.start')}
                      onClick={() => {
                        void handleTaskAction('start', task.id);
                      }}
                    >
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              )}
              {statusActionVisibility.showPauseComplete && (
                <>
                  <Tooltip title={t('myTasks.pause')}>
                    <IconButton
                      size="small"
                      aria-label={t('myTasks.pause')}
                      onClick={() => {
                        void handleTaskAction('pause', task.id);
                      }}
                    >
                      <PauseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('myTasks.complete')}>
                    <IconButton
                      size="small"
                      color="success"
                      aria-label={t('myTasks.complete')}
                      onClick={() => {
                        void handleTaskAction('complete', task.id);
                      }}
                    >
                      <CheckCircleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {statusActionVisibility.showResumeComplete && (
                <>
                  <Tooltip title={t('myTasks.resume')}>
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label={t('myTasks.resume')}
                      onClick={() => {
                        void handleTaskAction('resume', task.id);
                      }}
                    >
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('myTasks.complete')}>
                    <IconButton
                      size="small"
                      color="success"
                      aria-label={t('myTasks.complete')}
                      onClick={() => {
                        void handleTaskAction('complete', task.id);
                      }}
                    >
                      <CheckCircleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderItem = (item: UnifiedItem, context: TaskListContext) => {
    if (item.type === 'task') return renderTaskCard(item.data, context);
    return (
      <WorkOrderGroupCard
        key={item.workOrderId}
        workOrderId={item.workOrderId}
        services={item.services}
        expanded={expandedWorkOrders.has(item.workOrderId)}
        onToggle={() => toggleWorkOrderExpanded(item.workOrderId)}
        timerOwner={timerOwner}
      />
    );
  };

  const renderSection = (title: string, items: UnifiedItem[]) => {
    if (items.length === 0) return null;
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          {title}
        </Typography>
        {items.map((item) => renderItem(item, 'myDay'))}
      </Box>
    );
  };

  const isMyDayEmpty =
    ongoingItems.length === 0 && scheduledItems.length === 0 && completedItems.length === 0;
  const isOpenGeneralEmpty = openGeneralItems.length === 0;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400 }}>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 2 }}>
        {t('myTasks.title')}
      </Typography>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 3, md: 4 }}
        alignItems="flex-start"
        divider={
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: 'none', md: 'block' } }}
          />
        }
      >
        {/* My day — work orders + tasks assigned to me for selected date */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" fontWeight={600}>
              {t('myTasks.myDay')}
            </Typography>
            {canViewOthers && (
              <Autocomplete
                size="small"
                sx={{ minWidth: 220 }}
                options={staffUsers}
                groupBy={(u) => (u.isSchedulable === true ? t('myTasks.pickerMechanics') : t('myTasks.pickerOthers'))}
                getOptionLabel={(u) => `${u.firstName} ${u.lastName}`}
                value={selectedStaffUser}
                onChange={(_, val) => setSelectedStaffUserId(val?.id ?? null)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label={t('myTasks.viewUser')} />
                )}
              />
            )}
          </Stack>

          {/* Date navigation */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Tooltip title={t('myTasks.prevDay')}>
              <IconButton onClick={handlePrevDay} size="small" aria-label={t('myTasks.prevDay')}>
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('myTasks.goToday')}>
              <span>
                <IconButton
                  onClick={handleGoToday}
                  size="small"
                  disabled={isSelectedToday}
                  aria-label={t('myTasks.goToday')}
                >
                  <TodayIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('myTasks.nextDay')}>
              <IconButton onClick={handleNextDay} size="small" aria-label={t('myTasks.nextDay')}>
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="subtitle1" sx={{ ml: 1, textTransform: 'capitalize' }}>
              {dateLabel}
            </Typography>
          </Stack>

          {/* Shift info */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              mb: 2,
              px: 2,
              py: 1,
              borderRadius: 1,
              bgcolor: myShift ? 'action.hover' : 'transparent',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <WorkHistoryIcon fontSize="small" color={myShift ? 'primary' : 'disabled'} />
            {myShift ? (
              <>
                <Typography variant="body2" fontWeight={500}>
                  {t('myTasks.shift')}: {t('myTasks.shiftHours', { start: myShift.startTime, end: myShift.endTime })}
                </Typography>
                <Chip size="small" label={myShift.groupName} variant="outlined" sx={{ backgroundColor: myShift.color + '20', borderColor: myShift.color }} />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('myTasks.noShift')}
              </Typography>
            )}
          </Stack>

          {isMyDayLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {!isMyDayLoading && isMyDayEmpty && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <TaskAltIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">{t('myTasks.noTasks')}</Typography>
              <Typography variant="body2" color="text.disabled">
                {t('myTasks.noTasksForDate')}
              </Typography>
            </Box>
          )}

          {!isMyDayLoading && !isMyDayEmpty && (
            <>
              {isSelectedToday && renderSection(t('myTasks.ongoing'), ongoingItems)}
              {isSelectedToday && ongoingItems.length > 0 && scheduledItems.length > 0 && (
                <Divider sx={{ mb: 2 }} />
              )}
              {renderSection(t('myTasks.scheduled'), scheduledItems)}
              {completedItems.length > 0 && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  {renderSection(t('myTasks.completedSection'), completedItems)}
                </>
              )}
            </>
          )}
        </Box>

        {/* Open general tasks — team-wide visibility */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
            spacing={1}
          >
            <Typography variant="h6" fontWeight={600}>
              {t('myTasks.openGeneralTasks')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              {t('myTasks.newGeneralTask')}
            </Button>
          </Stack>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={audienceFilter}
            onChange={(_, value: TaskAudienceFilter | null) => {
              if (value) setAudienceFilter(value);
            }}
            aria-label={t('tasks.audience')}
            sx={{ mb: 2 }}
          >
            {TASK_AUDIENCE_FILTER_OPTIONS.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                {t(option.labelKey)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {isOpenGeneralLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {!isOpenGeneralLoading && isOpenGeneralEmpty && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <TaskAltIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">
                {t('myTasks.noOpenGeneralTasks')}
              </Typography>
            </Box>
          )}

          {!isOpenGeneralLoading && !isOpenGeneralEmpty && (
            <Box>{openGeneralItems.map((item) => renderItem(item, 'openGeneral'))}</Box>
          )}
        </Box>
      </Stack>

      <CreateTaskDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />

      <EditTaskDialog
        open={!!editTask}
        task={editTask}
        onClose={() => setEditTask(null)}
      />
    </Box>
  );
}
